import gaiupApolloClient from "@/graphql/services/ApolloClient";
import {
  ReplaceProjectReferenceAreasDocument,
  ReplaceProjectReferenceAreasWithVersionDocument,
  type ReplaceProjectReferenceAreasMutation,
  type ReplaceProjectReferenceAreasMutationVariables,
  type ReplaceProjectReferenceAreasWithVersionMutation,
  type ReplaceProjectReferenceAreasWithVersionMutationVariables,
} from "@/graphql";

import type { ReferencedAreaEntryType } from "../types/ReferencedAreasType";
import { withVersionFallback } from "./projectSetupWrites";
import {
  buildReferenceAreasVersionInput,
  referenceAreaRows,
  type ProjectReferenceAreasWriteInput,
} from "./referenceAreaWriteInput";

export interface ProjectReferenceAreasWriteResult {
  rowIds: string[];
  deleted: number;
  /** false when the trail row could not be written and the plain document was used instead. */
  versionLogged: boolean;
}

export interface ReferenceAreasVersionMeta {
  /** The saved baseline's entries, so the trail row can say what moved; null logs every area. */
  previousEntries: ReferencedAreaEntryType[] | null;
  labelFor: (typeKey: string) => string;
}

/**
 * The project's named reference areas, written the way the API writes them (delete, re-insert) but
 * through the user role and with a trail row, so a settings save that changed nothing else no longer
 * needs the REST updatecalculationassumptions PUT for them. The building areas beside them
 * (project_building_area) are not here: the user role can only read that table.
 */
export const replaceProjectReferenceAreas = async (
  input: ProjectReferenceAreasWriteInput,
  meta: ReferenceAreasVersionMeta
): Promise<ProjectReferenceAreasWriteResult> => {
  const areas = referenceAreaRows(input);

  const map = (
    data: ReplaceProjectReferenceAreasMutation | ReplaceProjectReferenceAreasWithVersionMutation,
    versionLogged: boolean
  ): ProjectReferenceAreasWriteResult => {
    if (!data.inserted) throw new Error("The reference area rows were not returned by Hasura.");
    return {
      rowIds: data.inserted.returning.map((row) => row.id),
      deleted: data.deleted?.affected_rows ?? 0,
      versionLogged,
    };
  };

  return withVersionFallback<
    ReplaceProjectReferenceAreasWithVersionMutation,
    ReplaceProjectReferenceAreasMutation,
    ProjectReferenceAreasWriteResult
  >(
    async () =>
      (
        await gaiupApolloClient.mutate<
          ReplaceProjectReferenceAreasWithVersionMutation,
          ReplaceProjectReferenceAreasWithVersionMutationVariables
        >({
          mutation: ReplaceProjectReferenceAreasWithVersionDocument,
          variables: {
            projectId: input.projectId,
            areas,
            version: buildReferenceAreasVersionInput(input, meta.previousEntries, meta.labelFor),
          },
        })
      ).data,
    async () =>
      (
        await gaiupApolloClient.mutate<ReplaceProjectReferenceAreasMutation, ReplaceProjectReferenceAreasMutationVariables>({
          mutation: ReplaceProjectReferenceAreasDocument,
          variables: { projectId: input.projectId, areas },
        })
      ).data,
    (data) => map(data, true),
    (data) => map(data, false)
  );
};
